// ============================================================================
// Componente: AutomacaoConferenciaVendas.js
// Objetivo: Interface para conferência de Balancete vs SOVOS (Vendas ATOS)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { 
    Calculator, Download, AlertCircle, CheckCircle2, 
    ArrowLeft, Loader2, UploadCloud, Eraser, HelpCircle, 
    FileSpreadsheet 
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

// `embedded`: quando true, renderizada DENTRO do módulo Contabilidade (que já traz
// Sidebar + cabeçalho). Escondemos Sidebar/cabeçalho/offset próprios. Default false = página inteira.
const AutomacaoConferenciaVendas = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';

    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    const [arquivoBalancete, setArquivoBalancete] = useState(null);
    const [arquivosAtos, setArquivosAtos] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [mensagem, setMensagem] = useState(null);

    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('Aguardando início...');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    // Polling Progress
    useEffect(() => {
        let interval;
        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const res = await fetch(`/api/contabilidade/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                    });
                    const data = await res.json();
                    
                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = lastLog.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '');
                        setStatusText(cleanLog);

                        if (cleanLog.includes('Conectando')) setProgress(10);
                        else if (cleanLog.includes('mapa de lojas')) setProgress(20);
                        else if (cleanLog.includes('processar balancete')) setProgress(35);
                        else if (cleanLog.includes('processar ficheiros ATOS')) setProgress(50);
                        else if (cleanLog.includes('Consultando Vendas')) setProgress(70);
                        else if (cleanLog.includes('Cruzando')) setProgress(85);
                        else if (cleanLog.includes('detalhamento')) setProgress(95);
                        else if (cleanLog.includes('finalizado') || cleanLog.includes('Sucesso')) setProgress(100);
                    }
                } catch(e) {}
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPolling, executionId]);

    // --- FUNÇÃO LIMPAR ---
    const handleLimpar = () => {
        setArquivoBalancete(null);
        setArquivosAtos([]);
        setMensagem(null);
        setProgress(0);
        setStatusText('Aguardando início...');
        setExecutionId(null);
        setIsPolling(false);
        
        const inputBal = document.getElementById('input-bal');
        const inputAtos = document.getElementById('input-atos');
        if (inputBal) inputBal.value = '';
        if (inputAtos) inputAtos.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCarregando(true);
        setMensagem(null);
        setProgress(5);
        setStatusText('A preparar o ambiente...');
        
        const newExecutionId = Date.now().toString();
        setExecutionId(newExecutionId);
        setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newExecutionId);
        formData.append('arquivo_balancete', arquivoBalancete);
        for (let i = 0; i < arquivosAtos.length; i++) {
            formData.append('arquivos_atos', arquivosAtos[i]);
        }

        try {
            const response = await fetch('/api/contabilidade/conferencia-vendas', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });

            if (response.ok) {
                const blob = await response.blob();
                const contentDisposition = response.headers.get('content-disposition');
                let fileName = 'Conferencia_Balancete_SOVOS.xlsx';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?(.*?)"?(;|$)/);
                    if (match && match[1]) fileName = match[1].replace(/['"]/g, ''); 
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                
                // Dispara o Log após o download
                
                setProgress(100);
                setStatusText('Download Concluído!');
                setMensagem({ type: 'success', text: 'Conferência concluída! O download iniciou.' });
                
                // Limpeza silenciosa do form após o sucesso
                setArquivoBalancete(null);
                setArquivosAtos([]);
                document.getElementById('input-bal').value = '';
                document.getElementById('input-atos').value = '';

            } else {
                const errorData = await response.json();
                setMensagem({ type: 'error', text: errorData.error || 'Erro ao processar.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        } finally {
            setCarregando(false);
            setTimeout(() => { setIsPolling(false); }, 3000);
        }
    };

    const corModulo = '#16a34a'; // Verde
    
    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444'; // Vermelho
        if (mensagem?.type === 'success') return '#10b981'; // Verde
        return corModulo; // Verde Padrão (Processando)
    };

    const isFormValido = arquivoBalancete !== null;

    const uploadAreaStyle = {
        border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px 20px', 
        textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
        transition: 'all 0.2s ease', position: 'relative', height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center'
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}
            
            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>
                
                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex', 
                    alignItems: 'center', justifyContent: 'space-between', 
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20 
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> Contábil
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Calculator size={24} color={corModulo} /> Balancete vs SOVOS
                            </h2>
                            <button 
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#f0fdf4', color: corModulo, border: '1px solid #dcfce7', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-5xl mx-auto">
                    
                    <div style={{ backgroundColor: '#fff', padding: isMobile ? '20px' : '30px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                        
                        <div style={{ backgroundColor: '#f0fdf4', borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                            <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontSize: '15px', fontWeight: '700', marginBottom: '8px', margin: 0 }}>
                                <Calculator size={18} /> Conciliação de Vendas
                            </h4>
                            <p style={{ color: '#14532d', fontSize: '14px', lineHeight: '1.6', margin: 0, marginTop: '4px' }}>
                                Faça o upload do arquivo de Balancete e dos arquivos de vendas ATOS para realizar o cruzamento e conferência automática via sistema SOVOS.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                                
                                {/* 1. Arquivo de Balancete */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        1. Arquivo de Balancete
                                    </label>
                                    <div style={uploadAreaStyle}>
                                        <input 
                                            id="input-bal" type="file" accept=".xlsx"
                                            onChange={(e) => { setArquivoBalancete(e.target.files[0]); setMensagem(null); }} 
                                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} 
                                            required disabled={carregando}
                                        />
                                        
                                        {!arquivoBalancete ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '50%', color: '#94a3b8' }}>
                                                    <UploadCloud size={24} />
                                                </div>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Balancete (.xlsx)</p>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '50%', color: corModulo }}>
                                                    <FileSpreadsheet size={24} />
                                                </div>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{arquivoBalancete.name}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 2. Ficheiros ATOS */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        2. Ficheiros ATOS (Opcional)
                                    </label>
                                    <div style={uploadAreaStyle}>
                                        <input 
                                            id="input-atos" type="file" multiple accept=".csv"
                                            onChange={(e) => { setArquivosAtos(e.target.files); setMensagem(null); }} 
                                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} 
                                            disabled={carregando}
                                        />
                                        
                                        {arquivosAtos.length === 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '50%', color: '#94a3b8' }}>
                                                    <UploadCloud size={24} />
                                                </div>
                                                <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Ficheiros ATOS (.csv)</p>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '50%', color: corModulo }}>
                                                    <FileSpreadsheet size={24} />
                                                </div>
                                                <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: corModulo }}>{arquivosAtos.length} ficheiro(s) selecionado(s)</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>

                            {/* BOTÕES DE AÇÃO */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                                <button type="button" onClick={handleLimpar} disabled={carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '8px', backgroundColor: '#f1f5f9', color: '#475569', fontWeight: '600', border: '1px solid #cbd5e1', cursor: carregando ? 'not-allowed' : 'pointer' }}>
                                    <Eraser size={18} /> Limpar
                                </button>
                                <button type="submit" disabled={carregando || !isFormValido} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '8px', backgroundColor: carregando || !isFormValido ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || !isFormValido ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s', boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.2)' }}>
                                    {carregando ? <><Loader2 size={18} className="animate-spin" /> Processando...</> : <><Download size={18} /> Iniciar Conferência</>}
                                </button>
                            </div>
                        </form>

                        {/* ALERTAS */}
                        {mensagem && !carregando && (
                            <div style={{ backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`, color: mensagem.type === 'success' ? '#059669' : '#ef4444', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px', fontSize: '14px', fontWeight: '500' }}>
                                {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />} 
                                {mensagem.text}
                            </div>
                        )}
                    </div>

                    {/* CARD DE PROGRESSO */}
                    {(carregando || progress > 0) && (
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {carregando && <Loader2 size={24} color="#0ea5e9" className="animate-spin" />}
                                    {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                                    {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ width: '100%', height: '12px', backgroundColor: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ 
                                    width: `${progress}%`, 
                                    height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA CONFERÊNCIA DE VENDAS */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="conferencia-vendas" 
                showOnlyIds={['conferencia-vendas']} 
            />
        </div>
    );
};

export default AutomacaoConferenciaVendas;